import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { AuthType } from '@/models/auth';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useDeviceStore } from '@/stores/device';
import { useFriendRequestCountStore } from '@/stores/friend-request-count';
import { useLayoutStore } from '@/stores/layout-status';
import { useMessageStore } from '@/stores/message';
import { usePeriodicMessageStore } from '@/stores/periodic-message';
import useSearchHistoryStore from '@/stores/search-history';
import { AUTH_STORAGE_KEY } from '@/utils/constants';
import { purgeLogs } from '@/utils/logger';
import { queryClient } from '@/utils/query-client';

interface AuthState {
  isHydrated: boolean;
  auth: AuthType | null;
  authError: string | false | null;
}

interface AuthActions {
  setHydrated(hasHydrated: boolean): void;

  start(session: AuthType): void;

  stop(reason?: string | false): void;

  updateToken(token: string): void;

  updateTraceRate(traceRate: number): void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    immer((set, get) => ({
      isHydrated: false as boolean,
      auth: null as AuthType | null,
      authError: null as string | false | null,

      setHydrated(hasHydrated: boolean) {
        set({ isHydrated: hasHydrated });
      },

      start(auth: AuthType) {
        if (get()?.auth?.UserID !== auth.UserID) {
          clearPersonalStore();
        }

        set({ auth, authError: null });
      },
      stop(reason) {
        clearPersonalStore();
        set({ auth: null, authError: reason ?? null });
      },
      updateToken(token: string) {
        set((state) => {
          if (state.auth) {
            state.auth.Token = token;
          }
        });
      },
      updateTraceRate(traceRate: number) {
        set((state) => {
          if (state.auth) {
            state.auth.TraceRate = traceRate;
          }
        });
      },
    })),
    {
      name: AUTH_STORAGE_KEY,
      version: 3,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        auth: state.auth,
      }),
      onRehydrateStorage: () => (state) => state?.setHydrated(true),
      migrate: () => ({
        auth: null,
      }),
    },
  ),
);

export function clearPersonalStore() {
  queryClient.clear();
  usePeriodicMessageStore.getState().clear();
  useConversationSessionStore.getState().clear();
  useSearchHistoryStore.getState().clearAllSearchHistory();
  useMessageStore.getState().clearAll();
  useFriendRequestCountStore.getState().clearAllFriendRequestId();
  useDeviceStore.getState().clear();
  useLayoutStore.setState({ sessionKey: '' });

  purgeLogs(Number.POSITIVE_INFINITY);
}

export function getUserId() {
  return useAuthStore.getState().auth?.UserID;
}

export function useIsAuthenticated() {
  return useAuthStore((state) => !!state.auth);
}

export function isAuthenticated(): Promise<boolean> {
  return new Promise((resolve) => {
    const { isHydrated, auth } = useAuthStore.getState();
    if (isHydrated) return resolve(Boolean(auth));
    const unsubscribe = useAuthStore.subscribe(({ auth, isHydrated }) => {
      if (isHydrated) {
        resolve(Boolean(auth));
        unsubscribe();
      }
    });
  });
}
